import React, { useContext, useState } from 'react';
import { useStyletron } from 'baseui';
import { Button, KIND, SIZE } from 'baseui/button';
import _get from 'lodash/get';
import { AppContext } from 'commons-ui';

import { DASHBOARD_RESOURCES, DASHBOARD_SCOPES, PAGE_NAMES } from 'constant';
import usePermission from 'commons-ui/src/permission/usePermission';

import useService from './utils/useService';

const scopes = DASHBOARD_SCOPES[DASHBOARD_RESOURCES.mandateRefund];

type TTarget = { uid: number; mandateId: string; orderId: string; amount: number };
type TExclusion = { lineNo: number; uid: string; reason: string };
type TResolveResult = {
	targets: TTarget[];
	exclusions: TExclusion[];
	totalAmount: number;
	amountBreakdown: { amount: number; count: number }[];
};
type TExecuteRow = { uid: number; mandateId: string; orderId: string; status: string; error?: string };
type TExecuteResult = { rows: TExecuteRow[]; submitted: number; failed: number; unknown: number };

const downloadCsv = (filename: string, lines: (string | number)[][]) => {
	const csvString = lines.map((row) => row.join(',')).join('\n');
	const link = document.createElement('a');
	link.setAttribute('href', encodeURI(`data:text/csv;charset=utf-8,${csvString}`));
	link.setAttribute('download', filename);
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
};

const MandateRefundDashboard: React.FC<{}> = () => {
	const [css, theme] = useStyletron();
	const { showAlert, app } = useContext(AppContext);
	const { hasPermission } = usePermission();
	const { resolve, execute } = useService();

	const [uidFile, setUidFile] = useState<File | null>(null);
	const [inputFile, setInputFile] = useState<File | null>(null);
	const [resolved, setResolved] = useState<TResolveResult | null>(null);
	const [executed, setExecuted] = useState<TExecuteResult | null>(null);
	const [resolving, setResolving] = useState(false);
	const [executing, setExecuting] = useState(false);

	// The same hook the route guard uses. mandateRefund is on its bypass list, so
	// both buttons are open to anyone who can reach the dashboard; take the resource
	// off that list and these start honouring the resolve and execute permissions
	// without any change here.
	const can = (action: string) =>
		hasPermission({
			configPageName: PAGE_NAMES.dashboard,
			configResourceName: DASHBOARD_RESOURCES.mandateRefund,
			configActionName: action,
			appName: app.appName
		});

	const card = css({
		background: theme.colors.backgroundPrimary,
		border: `1px solid ${theme.colors.borderOpaque}`,
		borderRadius: '6px',
		padding: '20px',
		marginBottom: '20px'
	});
	const heading = css({ ...theme.typography.HeadingXSmall, margin: '0 0 4px' });
	const sub = css({ ...theme.typography.ParagraphSmall, color: theme.colors.contentSecondary, margin: '0 0 16px' });
	const row = css({ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' });
	const summary = css({
		marginTop: '16px',
		padding: '12px 14px',
		borderRadius: '4px',
		background: theme.colors.backgroundSecondary,
		...theme.typography.ParagraphSmall
	});

	const onResolve = async () => {
		if (!uidFile) {
			showAlert('Choose a CSV of uids first', { type: 'error' });
			return;
		}
		setResolving(true);
		setResolved(null);
		try {
			const result = await resolve(uidFile);
			const data: TResolveResult = _get(result, ['data', 'data']);
			if (!_get(result, ['data', 'success'])) {
				showAlert(`Error: ${_get(result, ['data', 'error'], 'resolve failed')}`, { type: 'error' });
			} else {
				setResolved(data);
				showAlert(`Resolved ${data.targets.length} rows. Download input.csv, then run step 2.`);
			}
		} catch (error: any) {
			showAlert(`Error: ${_get(error, ['response', 'data', 'error'], error.message)}`, { type: 'error' });
		}
		setResolving(false);
	};

	const onExecute = async () => {
		if (!inputFile) {
			showAlert('Choose the input.csv from step 1 first', { type: 'error' });
			return;
		}
		setExecuting(true);
		setExecuted(null);
		try {
			const result = await execute(inputFile);
			const data: TExecuteResult = _get(result, ['data', 'data']);
			if (!_get(result, ['data', 'success'])) {
				showAlert(`Error: ${_get(result, ['data', 'error'], 'execute failed')}`, { type: 'error' });
			} else {
				setExecuted(data);
				const unknownNote = data.unknown ? `, ${data.unknown} unknown` : '';
				showAlert(`Submitted ${data.submitted} rows, ${data.failed} failed${unknownNote}. Download result.csv.`);
			}
		} catch (error: any) {
			showAlert(`Error: ${_get(error, ['response', 'data', 'error'], error.message)}`, { type: 'error' });
		}
		setExecuting(false);
	};

	const downloadInputCsv = () => {
		if (!resolved) return;
		downloadCsv('input.csv', [['mandateId', 'orderid', 'uid'], ...resolved.targets.map((t) => [t.mandateId, t.orderId, t.uid])]);
	};

	const downloadUnprocessableCsv = () => {
		if (!resolved) return;
		downloadCsv('unprocessable.csv', [
			['lineNo', 'uid', 'reason'],
			...resolved.exclusions.map((e) => [e.lineNo, e.uid, e.reason])
		]);
	};

	const downloadResultCsv = () => {
		if (!executed) return;
		downloadCsv('result.csv', [
			['uid', 'mandateId', 'orderId', 'status', 'error'],
			...executed.rows.map((r) => [r.uid, r.mandateId, r.orderId, r.status, (r.error || '').replace(/,/g, ';')])
		]);
	};

	return (
		<div className={css({ maxWidth: '760px', padding: '24px' })}>
			<h2 className={css({ ...theme.typography.HeadingSmall, margin: '0 0 6px' })}>Mandate Refund</h2>
			<p className={sub}>
				Upload a list of user ids, check what came back, then refund it. Nothing is stored between the two steps &mdash; keep
				the files you download.
			</p>

			<div className={card}>
				<h3 className={heading}>Step 1 &middot; Resolve</h3>
				<p className={sub}>
					A CSV with a single <code>uid</code> column, one per row. A row may also read &ldquo;Not registered&rdquo; or
					&ldquo;User not found&rdquo;. Read only &mdash; no money moves.
				</p>
				<div className={row}>
					<input
						type='file'
						accept='.csv'
						aria-label='CSV of user ids'
						onChange={(e) => setUidFile(_get(e, ['target', 'files', 0], null))}
					/>
					<Button size={SIZE.compact} isLoading={resolving} disabled={!can(scopes?.resolve)} onClick={onResolve}>
						Resolve
					</Button>
				</div>

				{resolved && (
					<div className={summary}>
						<div>
							<strong>{resolved.targets.length}</strong> refundable &middot;{' '}
							<strong>{resolved.exclusions.length}</strong> excluded &middot; total{' '}
							<strong>&#8377;{resolved.totalAmount.toLocaleString('en-IN')}</strong>
						</div>
						<div className={css({ marginTop: '4px', color: theme.colors.contentSecondary })}>
							{resolved.amountBreakdown.map((b) => `${b.amount} x ${b.count}`).join('  ·  ') || 'no rows'}
						</div>
						<div className={css({ marginTop: '12px', display: 'flex', gap: '10px', flexWrap: 'wrap' })}>
							<Button
								size={SIZE.mini}
								kind={KIND.secondary}
								onClick={downloadInputCsv}
								disabled={!resolved.targets.length}>
								Download input.csv
							</Button>
							<Button
								size={SIZE.mini}
								kind={KIND.tertiary}
								onClick={downloadUnprocessableCsv}
								disabled={!resolved.exclusions.length}>
								Download unprocessable.csv
							</Button>
						</div>
					</div>
				)}
			</div>

			<div className={card}>
				<h3 className={heading}>Step 2 &middot; Execute</h3>
				<p className={sub}>
					Upload the <code>input.csv</code> from step 1. This refunds every row in it. The request stays open until all
					batches finish, which takes about three seconds per 25 rows.
				</p>
				<div className={row}>
					<input
						type='file'
						accept='.csv'
						aria-label='input.csv from step 1'
						onChange={(e) => setInputFile(_get(e, ['target', 'files', 0], null))}
					/>
					<Button size={SIZE.compact} isLoading={executing} disabled={!can(scopes?.execute)} onClick={onExecute}>
						Execute Refund
					</Button>
				</div>

				{executed && (
					<div className={summary}>
						<div>
							<strong>{executed.submitted}</strong> submitted &middot; <strong>{executed.failed}</strong> failed
							{executed.unknown > 0 && (
								<>
									{' '}
									&middot; <strong>{executed.unknown}</strong> unknown
								</>
							)}
						</div>
						<div className={css({ marginTop: '4px', color: theme.colors.contentSecondary })}>
							Submitted means the endpoint accepted the order id. What settled is in deposit_refunds.
							{executed.unknown > 0 &&
								' Unknown rows timed out on the way out: they may have been refunded anyway, so check deposit_refunds before sending them again.'}
						</div>
						<div className={css({ marginTop: '12px' })}>
							<Button size={SIZE.mini} kind={KIND.secondary} onClick={downloadResultCsv}>
								Download result.csv
							</Button>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default MandateRefundDashboard;
